import { Modal } from '@ui'
import { Box } from 'grommet'
import parse from 'html-react-parser'
import { useState } from 'react'

type Props = {
  onClose: () => void
  title?: string
  url?: string
  content?: string
  category?: string
}

// Helper function to get file extension
const getFileExtension = (url: string): string => {
  return url.split('.').pop()?.toLowerCase() || ''
}

// Helper function to determine file type
const getFileType = (extension: string): 'image' | 'pdf' | 'document' | 'video' | 'audio' | 'other' => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']
  const pdfExtensions = ['pdf']
  const documentExtensions = ['doc', 'docx', 'txt', 'rtf', 'odt']
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv']
  const audioExtensions = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a']

  if (imageExtensions.includes(extension)) return 'image'
  if (pdfExtensions.includes(extension)) return 'pdf'
  if (documentExtensions.includes(extension)) return 'document'
  if (videoExtensions.includes(extension)) return 'video'
  if (audioExtensions.includes(extension)) return 'audio'
  return 'other'
}

// Component to render different file types
const FileRenderer = ({ url, fileType }: { url: string; fileType: string }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = url
    link.download = url.split('/').pop() || 'document'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleLoad = () => {
    setIsLoading(false)
  }

  const handleError = () => {
    setIsLoading(false)
    setHasError(true)
  }

  switch (fileType) {
    case 'image':
      return (
        <div className="flex flex-col items-center">
          {isLoading && <div className="text-center py-4">Loading image...</div>}
          {hasError ? (
            <div className="text-center py-4 text-red-500">
              <p>Failed to load image</p>
              <button
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleDownload}
              >
                Download File
              </button>
            </div>
          ) : (
            <img
              alt="document"
              className="max-w-full max-h-[70vh] object-contain"
              onError={handleError}
              onLoad={handleLoad}
              src={url}
              style={{ display: isLoading ? 'none' : 'block' }}
            />
          )}
        </div>
      )

    case 'pdf':
      return (
        <div className="w-full h-[70vh]">
          <iframe
            className="w-full h-full border rounded"
            onError={handleError}
            onLoad={handleLoad}
            src={url}
            title="PDF Document"
          />
          {isLoading && <div className="text-center py-4">Loading PDF...</div>}
          {hasError && (
            <div className="text-center py-4 text-red-500">
              <p>Failed to load PDF</p>
              <button
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleDownload}
              >
                Download PDF
              </button>
            </div>
          )}
        </div>
      )

    case 'video':
      return (
        <div className="flex flex-col items-center">
          <video className="max-w-full max-h-[70vh]" controls onError={handleError} onLoadStart={handleLoad}>
            <source src={url} type={`video/${getFileExtension(url)}`} />
            Your browser does not support the video tag.
          </video>
          {isLoading && <div className="text-center py-4">Loading video...</div>}
          {hasError && (
            <div className="text-center py-4 text-red-500">
              <p>Failed to load video</p>
              <button
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleDownload}
              >
                Download Video
              </button>
            </div>
          )}
        </div>
      )

    case 'audio':
      return (
        <div className="flex flex-col items-center">
          <audio className="w-full max-w-md" controls onError={handleError} onLoadStart={handleLoad}>
            <source src={url} type={`audio/${getFileExtension(url)}`} />
            Your browser does not support the audio tag.
          </audio>
          {isLoading && <div className="text-center py-4">Loading audio...</div>}
          {hasError && (
            <div className="text-center py-4 text-red-500">
              <p>Failed to load audio</p>
              <button
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleDownload}
              >
                Download Audio
              </button>
            </div>
          )}
        </div>
      )

    case 'document':
    case 'other':
    default:
      return (
        <div className="text-center py-8">
          <div className="mb-4">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Document Preview Not Available</h3>
          <p className="text-gray-500 mb-4">This file type cannot be previewed in the browser.</p>
          <button
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            onClick={handleDownload}
          >
            Download File
          </button>
        </div>
      )
  }
}

export const ViewDocument = ({ onClose, content, title, url, category }: Props) => {
  const fileExtension = url ? getFileExtension(url) : ''
  const detectedFileType = getFileType(fileExtension)

  return (
    <Modal onClose={onClose} title={title || category?.replace(/_/g, ' ') || 'Document Viewer'}>
      <div className="w-full min-w-0 max-w-[550px] px-5 py-4 overflow-auto">
        {content ? (
          <Box className="p-4 flex-1 overflow-auto border rounded-xl border-boxBorder mt-3">{parse(content)}</Box>
        ) : url ? (
          <div className="w-full w-full min-w-0 max-w-[650px]">
            <FileRenderer fileType={detectedFileType} url={url} />
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No content or file to display</p>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ViewDocument
